/**
 * Resize arithmetic for the correspondence composer (#1069 UI-7, UI-8).
 *
 * The message field used to be a fixed `minHeight: 96 / maxHeight: 220` in the
 * stylesheet, so writing or pasting anything long meant composing through a
 * letterbox. UI-7 puts a drag handle on the field's top edge: dragging up makes
 * the field taller, and the message list — the only `flex: 1` child in the
 * panel's column — takes whatever is left, exactly as it already did while the
 * field auto-grew.
 *
 * UI-8 raises the drag's ceiling to ALL the space below the header, because the
 * product owner used the fixed `[96, 220]` and found it too small for reviewing
 * a long pasted message. That reintroduces a MEASURED maximum, which is the
 * thing UI-7 spent three review rounds removing — so read the next section
 * before touching it.
 *
 * ⚠️ ONE CONTAINER, MEASURED ONCE, THAT THE DRAG CANNOT RESIZE.
 *
 * UI-7's maximum was `listHeight + fieldHeight`: TWO children, measured
 * SEPARATELY, in TWO components, and BOTH moved by the drag. Every defect found
 * in two rounds of adversarial review was an interaction between the two:
 *
 *  - on a 300px region the drag ceiling was 132 while auto-grow reached 220, so
 *    the first ArrowUp or upward drag requested 221, clamped to 132, and the
 *    field DROPPED 88px on a gesture whose purpose was to make it bigger;
 *  - the same regime announced `aria-valuenow: 220` against `aria-valuemax:
 *    112` — an invalid range for the `separator` pattern;
 *  - the handle consumed 22px of the region it was measured from, so between
 *    280 and 302 it could mount, un-qualify itself and unmount: a setState
 *    feedback loop;
 *  - a squeezed drag could bank an unbounded height that materialised later;
 *  - and the guard written to catch the first of these was a tautology
 *    (`cap < COMPOSER_AUTO_GROW_MAX_HEIGHT` where the function body IS that
 *    constant).
 *
 * THE DEFECT WAS NEVER THE ARITHMETIC. It was that the measured quantity was
 * DOWNSTREAM OF THE DRAG. So UI-8's measurement is taken from the ONE container
 * that holds the message list and the composer — a `flex: 1` child of the
 * panel's column, whose height is decided by the panel and its fixed chrome and
 * by nothing the composer does. The drag moves the boundary INSIDE that
 * container; it cannot change the container. That single property removes the
 * feedback loop, the mid-drag drift and the two-owner problem together.
 *
 * ⚠️ AND THE ONE INVARIANT THAT KEEPS TWO CEILINGS FROM COMING BACK:
 * {@link composerAutoGrowMaxHeight} is DERIVED FROM {@link composerMaxHeight}
 * by `Math.min`, so the auto-grow ceiling can never sit ABOVE the drag ceiling.
 * That inequality — and only that inequality — is what made the 88px drop
 * possible: auto-grow put the field somewhere the drag was not allowed to
 * reach, so the first keypress meant to grow it collapsed it instead. The two
 * numbers may differ (a tall panel lets the drag go past 220 while typing still
 * stops there); they may never cross.
 *
 * The file stays a MODULE rather than folding into `Composer.tsx` so the clamp
 * remains pure and DOM-free: web supplies a pointer delta and native supplies a
 * touch delta, and both land on the same number without a browser in the loop.
 * NOTHING HERE MEASURES ANYTHING — the container's height arrives as an
 * argument, from the one `onLayout` in `CorrespondencePanel.tsx`.
 */

/**
 * The field's minimum, unchanged from the stylesheet it came out of. Sized for
 * a pasted multi-paragraph email: generous enough that a real message is
 * visible while it is written.
 *
 * It does NOT move with the container. A user who has dragged the box down to
 * read the conversation gets the same box back on every panel.
 */
export const COMPOSER_MIN_HEIGHT = 96;

/**
 * The maximum WHEN NOTHING HAS BEEN MEASURED, and the ceiling auto-grow never
 * passes at any panel height.
 *
 * Unchanged from the `maxHeight` the stylesheet carried before UI-7, so an
 * undragged field behaves exactly as it always did: it grows to 220 and then
 * scrolls. UI-8 gives the DRAG — and only the drag — a taller ceiling, taken
 * from the one container measurement described above.
 *
 * ⚠️ This is still the number the whole feature falls back to. Before a
 * measurement arrives, and forever if none ever does, the range is `[96, 220]`
 * and nothing is `undefined` or `NaN`.
 */
export const COMPOSER_MAX_HEIGHT = 220;

/** Keyboard resize increment for the drag handle (Shift multiplies it). */
export const COMPOSER_RESIZE_STEP = 16;

/** Shift+arrow multiplier, so a keyboard user can cross the range quickly. */
export const COMPOSER_RESIZE_COARSE_MULTIPLIER = 4;

/**
 * Vertical finger travel before the native handle claims the gesture.
 *
 * SCROLL WINS ON AMBIGUITY. The handle sits outside the message list, so it
 * cannot intercept a scroll that started in the list — but a responder that
 * grabs on touch-down still swallows a flick that merely began on the grip.
 * Claiming only after a deliberate vertical movement leaves the ambiguous case
 * to the scroller, which is the recoverable direction: a scroll that should
 * have been a resize costs one more attempt, a resize that should have been a
 * scroll loses the user their place in the thread.
 */
export const COMPOSER_DRAG_ACTIVATION_DY = 5;

/**
 * The DRAG's ceiling: the measured container, or 220 when there is no
 * measurement.
 *
 * `availableHeight` is the height of the one container holding the message list
 * and the composer. Passing it here is the ONLY way a measurement enters this
 * module — the function does no measuring of its own, which is what keeps every
 * height in the feature reproducible from two numbers in a test.
 *
 * `<= 0` reads as "not measured yet", not as "a container with no room". A
 * layout pass genuinely reports 0 between mount and first measure, and treating
 * that as a real container would pin the field at its minimum for one frame on
 * every open.
 *
 * The `Math.max` floor is not decoration: without it a container shorter than
 * 96 would return a maximum BELOW the minimum, and every clamp built on it
 * would invert — `aria-valuemin` above `aria-valuemax`, which is not a range at
 * all. UI-7 shipped that exact contradiction once.
 *
 * THE MESSAGE LIST HAS NO FLOOR HERE, deliberately. Nothing is subtracted for
 * it. The user asked for all the space, the list is allowed to reach zero, and
 * the drag reverses.
 *
 * ⚠️ KNOWN, AND NOT SETTLED BY THE SPEC: THIS IS THE FIELD'S MAXIMUM, NOT THE
 * DOCK'S.
 *
 * The composer dock is the field PLUS its own chrome — the notice line, the
 * grip, the actions row and the dock's padding, roughly 110px in reply shape
 * and more while composing. So the field reaches the container's height about
 * 110px AFTER the message list has already gone to zero, and in that last
 * stretch the dock is taller than the box it sits in: the grip stops following
 * the pointer (its top edge is already at the top of the region) and the Post
 * button is pushed below the bottom of the panel. Dragging back down reverses
 * it; nothing is lost and the header is never covered, because the region
 * begins below the fixed chrome and overflow runs downward.
 *
 * It is left this way ON PURPOSE. The frozen spec asks for the field's height
 * to equal the container's, and the only way to end the range where the list
 * hits zero instead is to subtract the dock's chrome — which is a MODEL of a
 * layout that differs between compose and reply shapes and moves when the
 * notice wraps. `.claude/rules/responsive-ui.md` records three layout fixes in
 * this codebase that each passed a plausible model and failed in the field, and
 * a second measurement is exactly what the frozen block forbids. If the product
 * owner wants the range to end where the list ends, that is one subtraction
 * here and a new number in the tests — and it is their call, not this file's.
 */
export const composerMaxHeight = (availableHeight?: number | null): number => {
  if (availableHeight == null || !Number.isFinite(availableHeight)) {
    return COMPOSER_MAX_HEIGHT;
  }
  const measured = Math.round(availableHeight);
  if (measured <= 0) return COMPOSER_MAX_HEIGHT;
  return Math.max(COMPOSER_MIN_HEIGHT, measured);
};

/**
 * AUTO-GROW's ceiling, which is a different number and must never be a larger
 * one.
 *
 * Typing still stops at 220 on any panel with room for it: growing the box to
 * fill the whole panel because somebody pasted an email is precisely the
 * behaviour the drag exists to make OPTIONAL. Only a deliberate resize gets the
 * extra space.
 *
 * ⚠️ IT IS DERIVED FROM THE DRAG CEILING BY `Math.min`, AND THAT IS THE WHOLE
 * SAFETY ARGUMENT. On a panel shorter than 220 the drag ceiling drops to the
 * container, and if auto-grow kept its flat 220 the field could be auto-grown
 * to a height the drag was not allowed to reach — so the first ArrowUp would
 * SHRINK it, which is UI-7's 88px drop reintroduced. Taking the smaller of the
 * two makes `autoGrow <= drag` true by construction rather than by argument.
 *
 * UI-7 deleted a function of this name for being half of a two-ceiling design.
 * This one is the opposite: it exists so the two ceilings cannot cross.
 */
export const composerAutoGrowMaxHeight = (
  availableHeight?: number | null,
): number => Math.min(COMPOSER_MAX_HEIGHT, composerMaxHeight(availableHeight));

/**
 * Clamp a requested field height into `[96, composerMaxHeight(available)]`.
 *
 * Total function: any input, any caller, always a usable number. The ONLY
 * height authority in the feature — the pointer drag, the touch drag and the
 * keyboard all end up here, which is what makes "one ceiling" a property of the
 * code rather than a claim about it.
 *
 * PURE, AND IT MEASURES NOTHING. The container's height is an argument. Omit it
 * and the range is `[96, 220]` — which is not a special case in the code, just
 * what {@link composerMaxHeight} returns when it has nothing to go on.
 */
export const clampComposerHeight = (
  requested: number,
  availableHeight?: number | null,
): number => {
  const want = Math.round(requested);
  // NaN only. The signed infinities are DELIBERATELY left to the clamp below:
  // a `!Number.isFinite(want)` guard would catch `+Infinity` first and turn
  // "grow as far as possible" — what an unbounded upward drag produces — into
  // the MINIMUM, the exact opposite of the request. `Math.max(+Infinity, 96)`
  // is `+Infinity` and `Math.min(+Infinity, max)` is `max`, which is where an
  // unbounded upward drag should land; `-Infinity` falls to the minimum by the
  // same arithmetic. NaN carries no direction at all, so it gets the safe
  // default rather than a guessed one.
  if (Number.isNaN(want)) return COMPOSER_MIN_HEIGHT;
  return Math.min(
    Math.max(want, COMPOSER_MIN_HEIGHT),
    composerMaxHeight(availableHeight),
  );
};
